import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Paperclip, X, FileText, Calendar as CalendarIcon } from 'lucide-react';
import { VACATION_TYPES, calculateBusinessDays } from '../mock';
import { toast } from 'sonner';

const VacationRequestForm = ({ onSubmit, onCancel }) => {
  const [type, setType] = useState('ferie');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const days = useMemo(() => calculateBusinessDays(startDate, endDate), [startDate, endDate]);
  const today = new Date().toISOString().slice(0, 10);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`File troppo grande: ${f.name}`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Seleziona data inizio e data fine');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error('La data di fine deve essere successiva alla data di inizio');
      return;
    }
    if (days <= 0) {
      toast.error('Il periodo selezionato non contiene giorni lavorativi');
      return;
    }
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 500));
    onSubmit({
      type, startDate, endDate, days, notes,
      attachments: files.map(f => ({ name: f.name, size: f.size }))
    });
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="vtype" className="text-sm font-medium">Tipo di richiesta</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger id="vtype" className="h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VACATION_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start" className="text-sm font-medium">Data inizio</Label>
          <div className="relative">
            <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              id="start"
              type="date"
              min={today}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-11 pl-9"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="end" className="text-sm font-medium">Data fine</Label>
          <div className="relative">
            <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              id="end"
              type="date"
              min={startDate || today}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-11 pl-9"
              required
            />
          </div>
        </div>
      </div>

      {days > 0 && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
          <div className="text-xs text-blue-900">
            <strong>Giorni lavorativi richiesti:</strong> <span className="text-base font-semibold">{days}</span> {days === 1 ? 'giorno' : 'giorni'}
          </div>
          <div className="text-[11px] text-blue-700 mt-0.5">I sabati e le domeniche non sono conteggiati</div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="notes" className="text-sm font-medium">Note (opzionale)</Label>
        <Textarea
          id="notes"
          placeholder="Aggiungi informazioni aggiuntive (motivo, contatti di emergenza, handover...)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="resize-none"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Allegati (opzionale)</Label>
        <label className="flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-slate-300 text-sm text-slate-600 hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer transition-colors">
          <Paperclip className="w-4 h-4" />
          <span>Carica documenti (PDF, immagini)</span>
          <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileChange} className="hidden" />
        </label>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {files.map((f, i) => (
              <div key={`${f.name}-${f.size}-${i}`} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs">
                <FileText className="w-3.5 h-3.5 text-slate-600" />
                <span className="font-medium truncate max-w-[180px]">{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        )}
        <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800">
          {submitting ? 'Invio in corso...' : 'Invia richiesta'}
        </Button>
      </div>
    </form>
  );
};

export default VacationRequestForm;
